import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import trackEvents from '@/helpers/trackEvents';
import * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';
import QuestionnaireFormContext, { QuestionnaireFormContextType } from './QuestionnaireFormContext';
import useQuestionnaireContext from '../useQuestionnaireContext';
import parseFieldKey from '../../form/helpers/parseFieldKey';
import areValuesEqual from '../../form/helpers/areValuesEqual';
import isEmptyValue from '../../form/helpers/isEmptyValue';

type QuestionnaireFormProviderProps = {
  formMethods: QuestionnaireFormContextType['formMethods'];
  id: string;
};

const isRowFieldKey = (rowFieldKey: string, fullFieldKey: string): boolean => (
  fullFieldKey === rowFieldKey
  || fullFieldKey.startsWith(`${rowFieldKey}[`)
  || fullFieldKey.startsWith(`${rowFieldKey}.`)
);

const QuestionnaireFormProvider: React.FC<QuestionnaireFormProviderProps> = ({
  children,
  formMethods,
  id,
}) => {
  trackEvents.track('Render QuestionnaireFormProvider', id);
  const {
    fieldsByKey,
    questionnaireResponses,
  } = useQuestionnaireContext();
  const fieldsByKeyRef = useRef(fieldsByKey);
  fieldsByKeyRef.current = fieldsByKey;
  const responseSourcesRef = useRef<Record<string, QuestionnaireApiModels.QuestionnaireResponse['source']>>({});
  const getResponseSource = useCallback<QuestionnaireFormContextType['getResponseSource']>((fieldKey) => responseSourcesRef.current[fieldKey], []);
  const setResponseSource = useCallback<QuestionnaireFormContextType['setResponseSource']>((fieldKey, source) => {
    responseSourcesRef.current[fieldKey] = source;
  }, []);
  const getBulkResponseSources = useCallback<QuestionnaireFormContextType['getBulkResponseSources']>(args => {
    if (!args) {
      return responseSourcesRef.current;
    }
    const subFieldSources: ReturnType<QuestionnaireFormContextType['getBulkResponseSources']> = {};
    Object.entries(responseSourcesRef.current).forEach(([fullFieldKey, source]) => {
      if (isRowFieldKey(args.rowFieldKey, fullFieldKey)) {
        subFieldSources[fullFieldKey] = source;
      }
    });
    return subFieldSources;
  }, []);
  const mergeResponseSources = useCallback<QuestionnaireFormContextType['mergeResponseSources']>((sources) => {
    Object.entries(sources).forEach(([key, source]) => {
      responseSourcesRef.current[key] = source;
    });
  }, []);

  const autofillStatusRef = useRef<Record<string, boolean>>({});
  const getAutofillStatus = useCallback<QuestionnaireFormContextType['getAutofillStatus']>((fieldKey) => autofillStatusRef.current[fieldKey] ?? false, []);
  const setAutofillStatus = useCallback<QuestionnaireFormContextType['setAutofillStatus']>((fieldKey, status) => { autofillStatusRef.current[fieldKey] = status; }, []);
  const getBulkAutofillStatuses = useCallback<QuestionnaireFormContextType['getBulkAutofillStatuses']>((args) => {
    if (!args) {
      return autofillStatusRef.current;
    }
    const subFieldStatuses: ReturnType<QuestionnaireFormContextType['getBulkAutofillStatuses']> = {};
    Object.entries(autofillStatusRef.current).forEach(([fullFieldKey, status]) => {
      if (isRowFieldKey(args?.rowFieldKey, fullFieldKey)) {
        subFieldStatuses[fullFieldKey] = status;
      }
    });
    return subFieldStatuses;
  }, []);
  const mergeAutofillStatuses = useCallback<QuestionnaireFormContextType['mergeAutofillStatuses']>((statuses) => {
    Object.entries(statuses).forEach(([key, status]) => {
      autofillStatusRef.current[key] = status;
    });
  }, []);

  const aiRequestIdRef = useRef<Record<string, string | null>>({});
  const getAiRequestId = useCallback<QuestionnaireFormContextType['getAiRequestId']>((fieldKey): string | null => aiRequestIdRef.current[fieldKey] ?? null, []);
  const setAiRequestId = useCallback<QuestionnaireFormContextType['setAiRequestId']>((fieldKey, aiRequestIdLocal) => {
    aiRequestIdRef.current[fieldKey] = aiRequestIdLocal;
  }, []);

  const getBulkAiRequestIds = useCallback<QuestionnaireFormContextType['getBulkAiRequestIds']>((args) => {
    if (!args) {
      return aiRequestIdRef.current;
    }
    const subFieldAiRequestIds: ReturnType<QuestionnaireFormContextType['getBulkAiRequestIds']> = {};
    Object.entries(aiRequestIdRef.current).forEach(([fullFieldKey, aiRequestId]) => {
      if (isRowFieldKey(args.rowFieldKey, fullFieldKey)) {
        subFieldAiRequestIds[fullFieldKey] = aiRequestId;
      }
    });
    return subFieldAiRequestIds;
  }, []);
  const mergeAiRequestIds = useCallback<QuestionnaireFormContextType['mergeAiRequestIds']>((aiRequestIds) => {
    Object.entries(aiRequestIds).forEach(([key, aiRequestId]) => {
      aiRequestIdRef.current[key] = aiRequestId;
    });
  }, []);

  const explanationHtmlRef = useRef<Record<string, string | null>>({});
  const getExplanationHtml = useCallback<QuestionnaireFormContextType['getExplanationHtml']>((fieldKey) => explanationHtmlRef.current[fieldKey] ?? null, []);
  const setExplanationHtml = useCallback<QuestionnaireFormContextType['setExplanationHtml']>((fieldKey, explanationHtml) => {
    explanationHtmlRef.current[fieldKey] = explanationHtml;
  }, []);

  useEffect(() => {
    const newFieldSources: Record<string, QuestionnaireApiModels.QuestionnaireResponse['source']> = {};
    const newAiRequestIds: Record<string, string | null> = {};
    const newAutofillStatuses: Record<string, boolean> = {};
    const newExplanationHtmls: Record<string, string | null> = {};
    (questionnaireResponses ?? []).forEach(fieldResponse => {
      if (fieldResponse.fieldTemplate.type !== 'list' && fieldResponse.fieldTemplate.type !== 'table') {
        newFieldSources[fieldResponse.fieldTemplate.key] = fieldResponse.source;
        newAiRequestIds[fieldResponse.fieldTemplate.key] = fieldResponse.aiRequestId ?? null;
        newAutofillStatuses[fieldResponse.fieldTemplate.key] = fieldResponse.hasAutofillRun ?? false;
        newExplanationHtmls[fieldResponse.fieldTemplate.key] = fieldResponse.explanationHtml ?? null;
      } else {
        const listSources = fieldResponse.listResponses?.reduce(
          (acc: Record<string, QuestionnaireApiModels.QuestionnaireResponse['source']>, item) => ({
            ...acc,
            [`${fieldResponse.fieldTemplate.key}[${item.index}].${item.fieldTemplate.key}`]: item.source,
          }),
          {},
        );
        const listAiRquests = fieldResponse.listResponses?.reduce(
          (acc: Record<string, string | null>, item) => {
            if(item.source!=='ai') {
              return acc;
            }
            return (
              {
                ...acc,
                [`${fieldResponse.fieldTemplate.key}[${item.index}].${item.fieldTemplate.key}`]: item.aiRequestId ?? null,
              }
            );
          },
          {},
        );
        const listAutofillStatuses = fieldResponse.listResponses?.reduce(
          (acc: Record<string, boolean>, item) => ({
            ...acc,
            [`${fieldResponse.fieldTemplate.key}[${item.index}].${item.fieldTemplate.key}`]: item.hasAutofillRun ?? false,
          }),
          {},
        );
        const listExplanationHtmls = fieldResponse.listResponses?.reduce((acc: Record<string, string | null>, item) => ({
          ...acc,
          [`${fieldResponse.fieldTemplate.key}[${item.index}].${item.fieldTemplate.key}`]: item.explanationHtml ?? null,
        }), {});
        Object.assign(newFieldSources, listSources);
        Object.assign(newAiRequestIds, listAiRquests);
        Object.assign(newAutofillStatuses, listAutofillStatuses);
        Object.assign(newExplanationHtmls, listExplanationHtmls);
      }
    });
    responseSourcesRef.current = newFieldSources;
    aiRequestIdRef.current = newAiRequestIds;
    autofillStatusRef.current = newAutofillStatuses;
    explanationHtmlRef.current = newExplanationHtmls;
  }, [questionnaireResponses]);

  const setValue = useCallback<QuestionnaireFormContextType['setValue']>(
    ({ key, value, source, aiRequestId, hasAutofillRun = false, explanationHtml }): void => {
      const currentValue = formMethods.getValues(key);

      // Because nested objects become dirty if strict equal changes, even if deep stays the same.
      // Check for deep equality before updating the RHF value.
      const currentAutofillStatus = getAutofillStatus(key);
      if (areValuesEqual(currentValue, value) && hasAutofillRun === currentAutofillStatus) {
        return;
      }

      // If the current value is not set, and the new value is "empty" no need to set it.
      if ((currentValue === undefined || currentValue === null) && hasAutofillRun === currentAutofillStatus) {
        const parsedKey = parseFieldKey(key);
        const fieldTemplate = fieldsByKeyRef.current[parsedKey.fieldKey];
        if (isEmptyValue(value, fieldTemplate)) {
          return;
        }
      }

      // @todo - it would be nice if we checked the source here and didn't allow setting if "manual" unless new source is also "manual"

      if (source) {
        setResponseSource(key, source);
      }
      if(aiRequestId) {
        setAiRequestId( key, aiRequestId);
      }
      setAutofillStatus(key, hasAutofillRun);
      if (explanationHtml) {
        setExplanationHtml(key, explanationHtml);
      }

      formMethods.setValue(key, value, { shouldDirty: true, shouldTouch: true });
    },
    [formMethods, setResponseSource, setAiRequestId, setAutofillStatus, setExplanationHtml, getAutofillStatus],
  );

  const values = useMemo(() => {
    trackEvents.track('QuestionnaireFormContext - valuesMemoized');
    const newContextValues: QuestionnaireFormContextType = {
      formContextId: id,
      formMethods,
      getAiRequestId,
      getAutofillStatus,
      getBulkAiRequestIds,
      getBulkAutofillStatuses,
      getBulkResponseSources,
      getExplanationHtml,
      getResponseSource,
      mergeAiRequestIds,
      mergeAutofillStatuses,
      mergeResponseSources,
      setAiRequestId,
      setAutofillStatus,
      setExplanationHtml,
      setResponseSource,
      setValue,
    };
    return newContextValues;
  }, [
    id,
    formMethods,
    getAiRequestId,
    getBulkAiRequestIds,
    getBulkResponseSources,
    getExplanationHtml,
    getResponseSource,
    mergeAiRequestIds,
    mergeResponseSources,
    setAiRequestId,
    setExplanationHtml,
    setResponseSource,
    setValue,
    getAutofillStatus,
    getBulkAutofillStatuses,
    setAutofillStatus,
    mergeAutofillStatuses,
  ]);

  return (
    <QuestionnaireFormContext.Provider value={values}>
      {children}
    </QuestionnaireFormContext.Provider>
  );
};

export default QuestionnaireFormProvider;
